// LoginPage — the only place /login lives.
//
// Outside the app's main Layout (no top bar, no sidebar) so the user
// is never staring at an app shell they can't interact with. On
// successful submit we hydrate the auth store, then bounce to the
// ``?next=`` path the api.ts redirect originally captured (or /
// when the user landed here directly).

import { Sparkles } from 'lucide-react'
import { useEffect, useState, type FormEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ApiError, api } from '@/lib/api'
import { useAuthStore } from '@/stores/useAuthStore'

export function LoginPage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const signIn = useAuthStore((s) => s.signIn)
  const authEnabled = useAuthStore((s) => s.authEnabled)
  const ready = useAuthStore((s) => s.ready)
  const currentUser = useAuthStore((s) => s.user)

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const next = params.get('next') || '/'

  // If auth is disabled, or the user is already signed in, never let
  // the login page render — it would be a dead-end. Wait for the
  // initial hydration round-trip before deciding (else we'd flicker
  // /login on every cold load).
  useEffect(() => {
    if (!ready) return
    if (!authEnabled || currentUser) {
      navigate(next, { replace: true })
    }
  }, [ready, authEnabled, currentUser, next, navigate])

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      const session = await api.auth.login(username.trim(), password)
      signIn(session)
      navigate(next, { replace: true })
    } catch (err) {
      if (err instanceof ApiError && err.code === 'auth.invalid_credentials') {
        setError('Invalid username or password.')
      } else if (err instanceof ApiError && err.code === 'auth.not_configured') {
        setError('Authentication is not configured on the server.')
      } else {
        setError('Could not sign in. Please try again.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-100 p-6">
      <Card className="w-full max-w-sm p-8 space-y-6 bg-slate-900/80 border-slate-800">
        <div className="flex items-center gap-2.5">
          <span className="w-8 h-8 rounded-md bg-gradient-to-br from-indigo-500 to-fuchsia-500 flex items-center justify-center shadow-lg">
            <Sparkles className="h-4 w-4 text-white" />
          </span>
          <div>
            <div className="font-semibold tracking-tight">CARBON</div>
            <div className="text-xs text-slate-400">Sign in to continue</div>
          </div>
        </div>

        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <div className="space-y-1.5">
            <Label htmlFor="login-username">Username</Label>
            <Input
              id="login-username"
              name="username"
              autoComplete="username"
              autoFocus
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={submitting}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="login-password">Password</Label>
            <Input
              id="login-password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={submitting}
            />
          </div>

          {error && (
            <p
              role="alert"
              className="text-sm text-red-400 bg-red-950/40 border border-red-900/60 rounded-md px-3 py-2"
            >
              {error}
            </p>
          )}

          <Button
            type="submit"
            className="w-full"
            disabled={submitting || !username || !password}
          >
            {submitting ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>
      </Card>
    </div>
  )
}
